import { ChangeEvent, useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import { Checkbox } from 'primereact/checkbox';
import { Dropdown } from 'primereact/dropdown';
import { InputText } from 'primereact/inputtext';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';

import styles from './EditDivision.module.scss';
import AddDueDate from '../addDueDate/AddDueDate';
import { DivisionDetailsResponseModel } from '../../../models/divisions/DivisionDetailsResponseModel';
import { DivisionDueDatesResponseModel } from '../../../models/divisions/DivisionDueDatesResponseModel';
import { DivisionUpdateRequestModel } from '../../../models/divisions/DivisionUpdateRequestModel';
import { DropdownOption } from '../../../models/common/DropdownOption';
import { HttpService, IHttpService } from '../../../services/httpService';
import {
	INotifierService,
	NotifierService
} from '../../../services/notifierService';
import { Routes } from '../../../routes';
import { nameof } from '../../../shared/utility';

const EditDivision = (): JSX.Element => {
	const { id } = useParams<{ id: string }>();

	const [isLoading, setIsLoading] = useState<boolean>(false);

	const [divisionDetails, setDivisionDetails] =
		useState<DivisionDetailsResponseModel>({
			id: 0,
			code: '',
			description: '',
			notificationsEnabled: true,
			inputProductTypeId: 0,
			inputProductTypeDescription: '',
			divisionDueDates: []
		});

	const [productTypesOptions, setProductTypesOptions] = useState<
		DropdownOption[]
	>([]);

	const [description, setDescription] = useState<string>('');
	const [notificationsEnabled, setNotificationsEnabled] =
		useState<boolean>(true);
	const [inputProductTypeId, setInputProductTypeId] = useState<number>(0);
	const [shouldReload, setShouldReload] = useState<boolean>(false);

	const navigate = useNavigate();

	const httpService: IHttpService = useMemo(() => new HttpService(), []);
	const notifierService: INotifierService = useMemo(
		() => new NotifierService(),
		[]
	);

	const fetchDivision = useCallback(() => {
		httpService
			.get<DivisionDetailsResponseModel>(`divisions/${id}`, undefined)
			.then((division) => {
				setDivisionDetails(division);
				setDescription(division.description);
				setNotificationsEnabled(division.notificationsEnabled);
				setInputProductTypeId(division.inputProductTypeId);
			})
			.catch((err) => notifierService.error());
	}, [id, httpService, notifierService]);

	const handleEditDivision = async (): Promise<void> => {
		setIsLoading(true);

		const division: DivisionUpdateRequestModel = {
			description,
			notificationsEnabled,
			inputProductTypeId
		};

		try {
			await httpService.put(
				`divisions/${id}`,
				JSON.stringify(division),
				undefined,
				false
			);

			navigate(Routes.divisions.getPath());
		} catch (err) {
			notifierService.error(err);
		} finally {
			setIsLoading(false);
		}
	};

	const handleDeleteDueDate = useCallback(
		async (id: number): Promise<void> => {
			try {
				await httpService.delete(
					`divisions/deleteDueDate/${id}`,
					undefined
				);

				setShouldReload(true);
			} catch (err) {
				notifierService.error(err);
			} finally {
				setIsLoading(false);
			}
		},
		[httpService, notifierService]
	);

	useEffect(() => {
		fetchDivision();
	}, [id, httpService, notifierService, fetchDivision]);

	useEffect(() => {
		if (shouldReload) {
			fetchDivision();
			setShouldReload(false);
		}
	}, [shouldReload, fetchDivision, handleDeleteDueDate]);

	useEffect(() => {
		httpService
			.get<DropdownOption[]>('productTypes/dropdown', undefined)
			.then((options) => setProductTypesOptions(options))
			.catch((err) => notifierService.error());
	}, [httpService, notifierService]);

	const onRenderDeleteButton = (cellData: DivisionDueDatesResponseModel) => {
		return (
			<i
				className={`${styles.deleteIcon} pi pi-trash`}
				onClick={() => handleDeleteDueDate(cellData.id)}
			/>
		);
	};

	const getAddHeader = () => (
		<AddDueDate divisionId={Number(id)} setShouldReload={setShouldReload} />
	);

	return (
		<>
			<div className={styles.container}>
				<div className={styles.inputsContainer}>
					<div className={styles.content}>
						<label htmlFor='code'>Division code</label>
						<InputText
							id='code'
							readOnly
							placeholder='Division code'
							value={divisionDetails?.code}
						/>
					</div>

					<div className={styles.content}>
						<label htmlFor='description'>
							Division description
						</label>
						<InputText
							id='description'
							placeholder='Division description'
							value={description}
							onChange={(e: ChangeEvent<HTMLInputElement>) => {
								setDescription(e.target.value);
							}}
						/>
					</div>

					<div className={styles.content}>
						<label htmlFor='productType'>Input product type</label>
						<Dropdown
							id='productType'
							value={inputProductTypeId}
							options={productTypesOptions}
							onChange={(e) => {
								setInputProductTypeId(e.target.value);
							}}
						/>
					</div>

					<div className={styles.content}>
						<label htmlFor='notificationsEnabled'>
							Notifications enabled
						</label>
						<Checkbox
							id='notificationsEnabled'
							checked={notificationsEnabled}
							onChange={(e) => {
								setNotificationsEnabled(e.target.checked);
							}}
						/>
					</div>
				</div>
			</div>

			<button
				className={`btn primary ${styles.saveBtn}`}
				disabled={isLoading}
				onClick={handleEditDivision}
			>
				Save
			</button>
		</>
	);
};

export default EditDivision;
